import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogProps, Dropdown, DropdownOption } from 'ui-kit';
import { useDealsWithRemittances } from './getDealsWithRemittances_gql';

const PAYMENT_MONTH_FORMAT = 'YYYY-MM';
const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function buildYearOptions(): DropdownOption[] {
  const currentYear = new Date().getFullYear();
  const years: DropdownOption[] = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push({ value: String(y), label: String(y) });
  }
  return years;
}

export type RemittanceUploadParams = {
  dealId: string;
  paymentMonth: string;
};

interface RemittanceTapeUploadDialogProps {
  dialog: DialogProps;
  uploadfile?: (file: File, params: RemittanceUploadParams) => void | Promise<void>;
  file: File | null;
}

export const RemittanceTapeUploadDialog = ({
  dialog,
  uploadfile,
  file,
}: RemittanceTapeUploadDialogProps) => {
  const [selectedDealId, setSelectedDealId] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>(String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const prevIsOpen = useRef(dialog.isOpen);

  useEffect(() => {
    if (dialog.isOpen && !prevIsOpen.current) {
      setSelectedDealId('');
      setSelectedMonth('');
      setSelectedYear(String(new Date().getFullYear()));
      setUploadError(null);
    }
    prevIsOpen.current = dialog.isOpen;
  }, [dialog.isOpen]);

  const { data: dealsData, loading: dealsLoading } = useDealsWithRemittances();

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => buildYearOptions(), [currentYear]);

  const dealOptions: DropdownOption[] = useMemo(() => {
    const deals = dealsData?.getDealsWithRemittances ?? [];
    return deals.map((deal) => ({
      value: deal.id,
      label: deal.listing?.name ?? deal.id,
    }));
  }, [dealsData]);

  const paymentMonth = useMemo(() => {
    if (!selectedYear || !selectedMonth) return '';
    return `${selectedYear}-${selectedMonth}`;
  }, [selectedYear, selectedMonth]);

  const canSubmit = Boolean(file && selectedDealId && paymentMonth && paymentMonth.length === 7);

  const handleStartUpload = async () => {
    setUploadError(null);
    if (!file || !selectedDealId || !paymentMonth) return;
    try {
      await uploadfile?.(file, { dealId: selectedDealId, paymentMonth });
      dialog.setIsOpen(false);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  return (
    <Dialog dialog={dialog}>
      <div className="pb-2.5 text-[18px] font-medium leading-[36px] text-white">
        You&apos;ve chosen the following tape(s) to upload...
      </div>
      <div className="border-t border-gray-600" />
      <div className="py-2.5 text-sm font-normal leading-5 tracking-normal text-white">
        {file?.name}
      </div>
      <div className="py-2.5 text-sm font-normal leading-5 tracking-normal text-white">
        Select an Executed Deal in remittance that this tape is associated with
      </div>
      <div className="pt-2.5">
        <Dropdown
          options={dealOptions}
          placeholder={dealsLoading ? 'Loading deals…' : 'Select Deal'}
          value={selectedDealId}
          onValueChange={(option) => setSelectedDealId(option.value)}
        />
      </div>
      <div className="py-2.5 text-sm font-normal leading-5 tracking-normal text-white">
        Payment month ({PAYMENT_MONTH_FORMAT})
      </div>
      <div className="flex gap-2 pt-2.5">
        <Dropdown
          options={yearOptions}
          placeholder="Year"
          value={selectedYear}
          onValueChange={(option) => setSelectedYear(option.value)}
        />
        <Dropdown
          options={MONTHS}
          placeholder="Month"
          value={selectedMonth}
          onValueChange={(option) => setSelectedMonth(option.value)}
        />
      </div>
      {paymentMonth && (
        <div className="pt-1 text-xs text-gray-400">
          {PAYMENT_MONTH_FORMAT}: {paymentMonth}
        </div>
      )}
      {uploadError && (
        <div className="pt-2 text-sm text-red-400" role="alert">
          {uploadError}
        </div>
      )}
      <DialogActions className="!justify-evenly">
        <Button
          size="medium"
          label="cancel"
          onClick={() => {
            setUploadError(null);
            dialog.setIsOpen(false);
          }}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          label="Start Upload"
          disabled={!canSubmit}
          onClick={handleStartUpload}
        >
          Start Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};
